import { useContext, useEffect, useState } from 'react';
import MainLayout from '../components/MainLayout';
import { FlowForm, useFlowFormGlobal, FlowFormLeaveContext, ModalDialog, ButtonBar } from 't-lib-ui/structure';
import { HelpPopup } from 't-lib-ui/structure';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { AppState } from '../context/general';
import { changeEmail, getCurrentAccount, getCurrentUser, getEmail, getCachedEmail, hasEncryptedEmail, logOut, requestEmailChange, updateAccount, verifyPassword, deleteAccount, getMode } from '../connector/auth';
import { MAX_ANON_FEEDS, clearAnonData } from '../connector/feed-engine';
import { goTo, RouterContext, Link } from 't-lib-ui/router';
import { TextButton, PasswordInput, Note, Input, Checkbox } from 't-lib-ui/widgets';
import { validateEmail, nonNull } from 't-lib-ui/helpers';
import { downloadOpml, downloadUserData } from '../connector/api';
import { EmailLength, PasswordLength } from '../schema-copy/auth-schema';
import { validatePassword } from '../schema-copy/security';
import { getPlanLimits, planDisplayNames } from '../schema-copy/userPlans';
import { getUserType, UserType, isSuperuser } from '../schema-copy/capabilities';
import QuestionMarkIcon from '../components/QuestionMarkIcon';
import PageHeader from '../components/PageHeader';
import HeaderTools from '../components/HeaderTools';
import LegalFooter from '../components/LegalFooter';
import FeedQuotaCard from '../components/FeedQuotaCard';
import { useFeedQuota } from '../connector/feed-quota';
import OfflineNotice from '../components/OfflineNotice';
import { useReadingOffline } from '../utils/useOfflineMode';
import './PreferencesPage.css';

const passwordErrorTexts: Record<string, string> = {
  missing: 'Please enter a password.',
  short: 'Too short. The password needs to be at least 8 characters long.',
  simple: 'Too simple. Use upper and lower case, digits and/or special characters.',
};

const repeatPasswordErrorTexts: Record<string, string> = {
  ...passwordErrorTexts,
  missing: 'Please repeat the password here.',
  unequal: 'Both passwords must match.',
};

const emailErrorTexts: Record<string, string> = {
  missing: 'Please enter an email address.',
  invalid: 'Please enter a valid email address.',
  unequal: 'Both email addresses must match.',
  conflict: 'That email is already in use.',
};

const codeErrorTexts: Record<string, string> = {
  missing: 'Please enter the code.',
  short: 'Each code should be 5 characters.',
};

function validatePasswordField(email: string, value: string): 'missing' | 'short' | 'simple' | undefined {
  if (!value.trim()) return 'missing';
  const errors = validatePassword(email, value);
  if (!errors.length) return undefined;
  if (['passwordNoWhitespace', 'passwordShort', 'passwordLong'].includes(errors[0])) return 'short';
  return 'simple';
}

const AccountPage: React.FC = () => {
  const readingOffline = useReadingOffline();
  const appState = useContext(AppState);
  const routerCtx = useContext(RouterContext);
  const user = appState.state.currentUser;
  const anon = getMode() == 'anon';
  const { count: liveFeedCount, atLimit: atFeedQuota } = useFeedQuota();
  const userPlan = user?.userPlan ?? 'free';
  // Anon: use the reactive live count (updates via onFeedsChange) so the displayed number doesn't stay
  // stale at 0 while the anon engine is still loading feeds after a navigation. Logged-in: the cached
  // user record has the count immediately, avoiding a stale-0 flash before the engine loads.
  const feedCount = anon ? liveFeedCount : (user?.feeds?.length ?? 0);
  const privateFeedCount = appState.feedEngine.getPrivateFeedCount();
  const newsletterFeedCount = appState.feedEngine.getNewsletterFeedCount();
  const { maxFeeds, maxPrivateFeeds, maxNewsletterFeeds, maxInactivityDays } = getPlanLimits(userPlan);

  // Prefer any email we already know (the user record, or the in-memory cache seeded from the address
  // typed at login) so it shows instantly — no cookie-authed /auth/email round-trip, which fails on
  // iOS PWAs that drop the cross-site authToken cookie.
  const [currentEmail, setCurrentEmail] = useState(user?.email || getCachedEmail() || '');
  // True while we hold an encrypted email blob but haven't decrypted it yet (e.g. right after a
  // reload, before /auth/email resolves) — so we can show "Decrypting email…" instead of "no email".
  const [emailLoading, setEmailLoading] = useState(() => !(user?.email || getCachedEmail()) && hasEncryptedEmail());
  // The external provider the account signs in with, if any.
  const [externalProvider, setExternalProvider] = useState<'Google'|'Apple'|undefined>();
  const [hasPassword, setHasPassword] = useState(true);
  const [jurisdiction, setJurisdiction] = useState<string | undefined>(undefined);
  const [accountLoaded, setAccountLoaded] = useState(false);
  const [showPlanHelp, setShowPlanHelp] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<string | undefined>();
  const [repeatPassword, setRepeatPassword] = useState('');
  const [repeatPasswordError, setRepeatPasswordError] = useState<string | undefined>();
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState<'idle' | 'success' | 'wrong' | 'error'>('idle');

  // Email change state
  const [emailPhase, setEmailPhase] = useState<'enterEmail' | 'enterCodes'>('enterEmail');
  const [newEmail, setNewEmail] = useState('');
  const [newEmailError, setNewEmailError] = useState<string | undefined>();
  const [emailPassword, setEmailPassword] = useState('');
  const [emailPasswordError, setEmailPasswordError] = useState<string | undefined>();
  const [emailCodeA, setEmailCodeA] = useState('');
  const [emailCodeB, setEmailCodeB] = useState('');
  const [emailCodeAError, setEmailCodeAError] = useState<string | undefined>();
  const [emailCodeBError, setEmailCodeBError] = useState<string | undefined>();
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'success' | 'wrongPw' | 'conflict' | 'badCodes' | 'timeout' | 'error'>('idle');

  // OPML export state (import lives on /a/import)
  const [opmlError, setOpmlError] = useState<string | undefined>();
  const [opmlExporting, setOpmlExporting] = useState(false);

  // Data export (GDPR Art. 20) state
  const [dataExportBusy, setDataExportBusy] = useState(false);
  const [dataExportError, setDataExportError] = useState<string | undefined>();

  // Delete-account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePhase, setDeletePhase] = useState<'confirm' | 'working' | 'success' | 'error'>('confirm');
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState<string | undefined>();
  const [deleteChecked, setDeleteChecked] = useState(false);
  const [deleteCheckedError, setDeleteCheckedError] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number; current?: string }>({ done: 0, total: 0 });
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | undefined>();

  // Anonymous-mode data dialog (shared by "log out" and "delete my data"): there's no server
  // account, so both just wipe the local anon storage.
  const [showAnonDataDialog, setShowAnonDataDialog] = useState(false);
  const [anonDialogMode, setAnonDialogMode] = useState<'logout' | 'delete'>('delete');
  const [anonDeleteDone, setAnonDeleteDone] = useState(false);

  useFlowFormGlobal();

  useEffect(() => {
    const auth = appState.state.authData;
    if (!auth) return;
    getCurrentAccount(auth).then(account => {
      setExternalProvider(account.appleId ? 'Apple' : account.googleId ? 'Google' : undefined);
      setHasPassword(account.hasPassword);
      setJurisdiction(account.jurisdiction);
      setAccountLoaded(true);
    }).catch(() => setAccountLoaded(true));
    // Resolve the displayed email by decrypting the stored blob via /auth/email (no plaintext kept).
    getEmail(auth).then(e => { if (e) setCurrentEmail(e); }).catch(() => {}).finally(() => setEmailLoading(false));
  }, []);

  const openPasswordDialog = () => {
    setOldPassword('');
    setNewPassword('');
    setRepeatPassword('');
    setNewPasswordError(undefined);
    setRepeatPasswordError(undefined);
    setPasswordStatus('idle');
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    const pwErr = validatePasswordField(currentEmail,newPassword);
    const rpErr = !repeatPassword ? 'missing' : newPassword != repeatPassword ? 'unequal' : undefined;
    setNewPasswordError(pwErr);
    setRepeatPasswordError(rpErr);
    if (pwErr || rpErr) return;

    const auth = appState.state.authData;
    if (!auth) return;

    setPasswordBusy(true);
    setPasswordStatus('idle');
    const result = await updateAccount(auth, {
      password: newPassword,
      ...(hasPassword ? { oldPassword } : {}),
    });
    setPasswordBusy(false);

    if (result == 'LoginFailed') {
      setPasswordStatus('wrong');
    } else if (result == 'LoginTimeout') {
      setPasswordStatus('error');
    } else {
      setPasswordStatus('success');
      setHasPassword(true);
      setTimeout(() => setShowPasswordDialog(false), 1500);
    }
  };

  const openEmailDialog = () => {
    setEmailPhase('enterEmail');
    setNewEmail('');
    setEmailPassword('');
    setEmailCodeA('');
    setEmailCodeB('');
    setNewEmailError(undefined);
    setEmailPasswordError(undefined);
    setEmailCodeAError(undefined);
    setEmailCodeBError(undefined);
    setEmailStatus('idle');
    setShowEmailDialog(true);
  };

  const handleRequestEmailChange = async () => {
    const trimmed = newEmail.trim();
    const neErr = !trimmed ? 'missing' : validateEmail(trimmed) ? 'invalid' : undefined;
    const pwErr = !emailPassword ? 'missing' : undefined;
    setNewEmailError(neErr);
    setEmailPasswordError(pwErr);
    if (neErr || pwErr) return;
    const auth = appState.state.authData;
    if (!auth || !currentEmail) return;

    setEmailBusy(true);
    setEmailStatus('idle');
    const result = await requestEmailChange(auth, currentEmail, trimmed, emailPassword);
    setEmailBusy(false);

    if (result == 'LoginFailed') setEmailStatus('wrongPw');
    else if (result == 'LoginTimeout') setEmailStatus('timeout');
    else if (result == 'Conflict') setEmailStatus('conflict');
    else {
      setEmailStatus('idle');
      setEmailPhase('enterCodes');
    }
  };

  const handleChangeEmail = async () => {
    const aErr = !emailCodeA ? 'missing' : emailCodeA.trim().length != 5 ? 'short' : undefined;
    const bErr = !emailCodeB ? 'missing' : emailCodeB.trim().length != 5 ? 'short' : undefined;
    setEmailCodeAError(aErr);
    setEmailCodeBError(bErr);
    if (aErr || bErr) return;
    const auth = appState.state.authData;
    if (!auth) return;

    setEmailBusy(true);
    setEmailStatus('idle');
    const result = await changeEmail(auth, newEmail.trim(), emailCodeA.trim(), emailCodeB.trim());
    setEmailBusy(false);

    if (result == 'LoginFailed' || result == 'TokenInvalid') setEmailStatus('badCodes');
    else if (result == 'LoginTimeout') setEmailStatus('timeout');
    else {
      setEmailStatus('success');
      setCurrentEmail(newEmail.trim()); // changeEmail() already refreshed the stored encrypted blob
      setTimeout(() => setShowEmailDialog(false), 1500);
    }
  };

  const handleLogout = () => {
    logOut();
    appState.setLoggedOut();
    goTo(routerCtx, '/', 'replace');
  };

  const openAnonDataDialog = (mode: 'logout' | 'delete') => {
    setAnonDialogMode(mode);
    setAnonDeleteDone(false);
    setShowAnonDataDialog(true);
  };

  const handleDeleteAnonData = () => {
    // Full wipe. logOut() now PRESERVES the anon-* keys (so authenticated logout can restore an
    // anon session), so for the explicit "delete my data" action we clear them first, then logOut()
    // wipes the rest — keyboard-tips flag, dismissed-card flags, cached widths, etc. — so a fresh
    // visitor starts truly clean.
    clearAnonData();
    logOut();
    setAnonDeleteDone(true);
    setTimeout(() => {
      setShowAnonDataDialog(false);
      // Swap in a fresh anon engine reading the now-empty storage, then return to the app.
      appState.setLoggedOut();
      goTo(routerCtx, '/', 'replace');
    }, 1200);
  };

  const openDeleteDialog = () => {
    setDeletePhase('confirm');
    setDeletePassword('');
    setDeletePasswordError(undefined);
    setDeleteChecked(false);
    setDeleteCheckedError(false);
    setDeleteProgress({ done: 0, total: 0 });
    setDeleteErrorMessage(undefined);
    setShowDeleteDialog(true);
  };

  const handleDeleteAccount = async () => {
    const auth = appState.state.authData;
    if (!auth) return;

    // Phase 1: confirm credentials
    if (hasPassword) {
      if (!deletePassword) { setDeletePasswordError('missing'); return; }
      const result = await verifyPassword(auth, deletePassword);
      if (result == 'LoginFailed') { setDeletePasswordError('wrong'); return; }
      if (result == 'LoginTimeout') { setDeletePasswordError('timeout'); return; }
    } else {
      // Google/Apple-only users have no password: a checkbox confirms instead
      if (!deleteChecked) { setDeleteCheckedError(true); return; }
    }

    // Phase 2: refresh the user record from the server so our snapshot of feed IDs
    // matches what the backend will see when we hit DELETE /api/user/self. The
    // cached appState.currentUser is set once at login and can drift from the truth
    // (other tabs / sessions, or simply being older than the engine state).
    let feedsSnapshot: string[];
    try {
      const fresh = await getCurrentUser(auth);
      feedsSnapshot = [...(fresh.feeds ?? [])];
    } catch (e) {
      setDeleteErrorMessage('Could not load your current feed list: ' + (e as Error).message);
      setDeletePhase('error');
      return;
    }
    setDeleteProgress({ done: 0, total: feedsSnapshot.length });
    setDeletePhase('working');

    for (let i = 0; i < feedsSnapshot.length; i++) {
      const feedId = feedsSnapshot[i];
      setDeleteProgress({ done: i, total: feedsSnapshot.length, current: feedId });
      const ok = await appState.feedEngine.unsubscribe(feedId);
      if (!ok) {
        setDeleteErrorMessage(`Could not unsubscribe from ${feedId}. Please try again.`);
        setDeletePhase('error');
        return;
      }
    }
    setDeleteProgress({ done: feedsSnapshot.length, total: feedsSnapshot.length });

    // All subscriptions are gone. Pause the engine so it stops polling for an
    // account we're about to delete (the next requests would 401 anyway).
    appState.feedEngine.pause();

    // Phase 3: hard-delete the account
    try {
      await deleteAccount(auth);
    } catch (e) {
      setDeleteErrorMessage((e as Error).message);
      setDeletePhase('error');
      return;
    }

    setDeletePhase('success');
    setTimeout(() => {
      setShowDeleteDialog(false);
      handleLogout();
    }, 1500);
  };

  const handleOpmlExport = async () => {
    if (opmlExporting) return;
    setOpmlError(undefined);
    setOpmlExporting(true);
    try {
      const auth = nonNull(appState.state.authData);
      await downloadOpml(auth);
    } catch (e) {
      setOpmlError('Export failed: ' + (e as Error).message);
    } finally {
      setOpmlExporting(false);
    }
  };

  const handleDataExport = async () => {
    if (dataExportBusy) return;
    setDataExportError(undefined);
    setDataExportBusy(true);
    try {
      const auth = nonNull(appState.state.authData);
      await downloadUserData(auth);
    } catch (e) {
      setDataExportError('Export failed: ' + (e as Error).message);
    } finally {
      setDataExportBusy(false);
    }
  };

  const capabilities = appState.state.authData?.selectedMembership?.capabilities;
  const userType = capabilities ? getUserType(capabilities) : undefined;
  const roleLabel = userType == UserType.Owner ? 'Owner' : userType == UserType.Superuser ? 'Superuser' : userType == UserType.Agent ? 'Agent' : undefined;
  const superuser = capabilities ? isSuperuser(capabilities) : false;
  const privileged = !!roleLabel || superuser;

  const planLabel = planDisplayNames[userPlan] ?? 'Free';

  return (
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed="account"
      right={readingOffline ? ({ onExpand }) => (
        // Everything here (password, email, plan, deletion…) needs the server, so nothing is shown.
        <div className="preferencesPage">
          <PageHeader title="Account" onMenu={onExpand} rightContent={<HeaderTools />} />
          <OfflineNotice>Your account can't be managed until you're back online.</OfflineNotice>
        </div>
      ) : ({ onExpand }) => (
        <div className="preferencesPage">
          <PageHeader title="Account" onMenu={onExpand} rightContent={<HeaderTools />} />

          {/* Account Section */}
          <FlowForm main className="preferencesSection">
            {atFeedQuota && (
              <div className="preferencesSectionContent">
                <FeedQuotaCard />
              </div>
            )}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Account</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  <div className="accountInfoRow">
                    <span className="accountInfoLabel">Email</span>
                    <span className="accountInfoValue">{anon ? 'Anonymous' : currentEmail ? currentEmail : emailLoading ? 'Decrypting email…' : '(No email set)'}</span>
                  </div>
                  {!anon && jurisdiction && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Data region</span>
                    <span className="accountInfoValue" data-testid="jurisdiction">{jurisdiction == 'EU' ? 'European Union (EU)' : jurisdiction == 'US' ? 'United States (US)' : jurisdiction}</span>
                  </div>}
                  {!anon && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Password</span>
                    <span className="accountInfoValue">{accountLoaded && !hasPassword ? '(not set)' : '********'}</span>
                  </div>}
                  {roleLabel && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Role</span>
                    <span className="accountInfoValue">{roleLabel}</span>
                  </div>}
                  {superuser && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Superuser</span>
                    <span className="accountInfoValue">Yes</span>
                  </div>}
                  <div className="preferenceButtonRow">
                    {anon
                      ? <>
                          <TextButton label="Log in" type="minor" onClick={() => { window.location.href = '/login'; }} />
                          <TextButton label="Sign up" type="major" focussable="sideway" onClick={() => { window.location.href = '/signup'; }} />
                          <TextButton label="Log out" type="minor" focussable="sideway" onClick={() => openAnonDataDialog('logout')} />
                        </>
                      : <>
                          <TextButton label="Change email" type="minor" enabled={accountLoaded && hasPassword ? 'enabled' : 'disabled'} onClick={openEmailDialog} />
                          <TextButton label={hasPassword ? "Change password" : "Set password"} type="minor" focussable="sideway" enabled={accountLoaded ? 'enabled' : 'disabled'} onClick={openPasswordDialog} />
                          <TextButton label="Log out" type="minor" focussable="sideway" onClick={() => goTo(routerCtx, '/logout')} />
                        </>
                    }
                  </div>
                  {anon && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '0.5rem 0 0' }}>You're browsing anonymously. Your feeds and reading history live only in this browser. Sign up for a free account to sync across devices.</p>}
                  {!anon && externalProvider && !hasPassword && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '0.5rem 0 0' }}>You signed in with {externalProvider}. Set a password to also log in with email.</p>}
                </div>
              </div>
            </div>

            {/* Plan Section */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Plan</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  <div className="accountInfoRow">
                    <span className="accountInfoLabel">Current plan</span>
                    <span className="accountInfoValue accountInfoClickable" onClick={() => setShowPlanHelp(true)}>
                      {anon ? 'Anonymous' : planLabel}
                      <button className="accountHelpButton" onClick={(e) => { e.stopPropagation(); setShowPlanHelp(true); }} title="About plans">
                        <QuestionMarkIcon size={14} />
                      </button>
                    </span>
                  </div>
                  <div className="accountInfoRow">
                    <span className="accountInfoLabel">Followed feeds</span>
                    <span className={`accountInfoValue${feedCount >= (anon ? MAX_ANON_FEEDS : maxFeeds) ? ' atLimit' : ''}`}>{feedCount} / {anon ? MAX_ANON_FEEDS : maxFeeds}</span>
                  </div>
                  {!anon && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Private feeds</span>
                    <span className={`accountInfoValue${privateFeedCount >= maxPrivateFeeds ? ' atLimit' : ''}`}>{privateFeedCount} / {maxPrivateFeeds}</span>
                  </div>}
                  {!anon && <div className="accountInfoRow">
                    <span className="accountInfoLabel">Newsletter Inboxes</span>
                    {maxNewsletterFeeds > 0
                      ? <span className={`accountInfoValue${newsletterFeedCount >= maxNewsletterFeeds ? ' atLimit' : ''}`}>{newsletterFeedCount} / {maxNewsletterFeeds}</span>
                      : <span className="accountInfoValue">Not on your plan</span>
                    }
                  </div>}
                  {anon && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '0.5rem 0 0' }}>Private feeds and newsletter inboxes are only available for registered users.</p>}
                </div>
                <HelpPopup show={showPlanHelp} title="Downread Plans" onClose={() => setShowPlanHelp(false)}>
                  {anon
                    ? <p><strong>Anonymous</strong> - Follow up to {MAX_ANON_FEEDS} feeds, kept only in this browser. Sign up for a free account to sync across devices and unlock private feeds and newsletter inboxes.</p>
                    : <p><strong>{planLabel}</strong> - Up to {maxFeeds} feeds (incl. {maxPrivateFeeds} private, {maxNewsletterFeeds} newsletter inboxes)
                        {userPlan == 'earlyAdopter' && <>, with {getPlanLimits('earlyAdopter').maxPrivateFeedStorageMb} MB of private feed and newsletter storage each. This plan stays free for you, forever. Thanks for being here early!</>}
                        {userPlan == 'free' && <>. All core features included.</>}
                        {userPlan == 'test' && <>. Internal plan for testing quotas.</>}
                        {(userPlan == 'pro' || userPlan == 'vip') && <>.</>}
                      </p>
                  }
                  {!anon && maxInactivityDays && <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                    Private feeds and newsletter inboxes are emptied after {maxInactivityDays} days without any activity on your account. Your account and your feed list stay.
                  </p>}
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                    Private feeds are feeds that will not be made visible to other users. They're fetched and stored only for you, so the per-plan limit is lower than the total.
                  </p>
                </HelpPopup>
              </div>
            </div>

            {/* Import / Export (OPML) Section */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Import / Export (OPML)</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    Import a feed list from another reader, or export your subscriptions as a standard OPML file.
                  </p>
                  <div className="preferenceButtonRow">
                    <TextButton
                      label="Import OPML…"
                      type="minor"
                      enabled={anon || opmlExporting ? 'disabled' : 'enabled'}
                      onClick={() => goTo(routerCtx, '/a/import')}
                    />
                    <TextButton
                      label="Export"
                      type="minor"
                      focussable="sideway"
                      enabled={anon || opmlExporting ? 'disabled' : 'enabled'}
                      busy={opmlExporting}
                      onClick={handleOpmlExport}
                    />
                  </div>
                  {anon && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                      Importing and exporting OPML requires a free account. Sign up to move your feeds in and out of Downread.
                    </p>
                  )}
                  {opmlError && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--error-color, #c33)' }}>
                      {opmlError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Download my data (GDPR Art. 20) Section */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">Download my data</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  {anon
                    ? <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                        In anonymous mode we don't store any data about you on our servers. Your subscriptions and reading history live only in this browser, so there's nothing to download here. Sign up for a free account to enable a full data export.
                      </p>
                    : <>
                        <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                          Download all data we hold about you as a single JSON file - your profile, subscriptions with feed metadata, folder structure, read state, starred items and read-later list. Structured for portability to another reader.
                        </p>
                        <div className="preferenceButtonRow">
                          <TextButton
                            label="Download (JSON)"
                            type="minor"
                            enabled={dataExportBusy ? 'disabled' : 'enabled'}
                            busy={dataExportBusy}
                            onClick={handleDataExport}
                          />
                        </div>
                        {dataExportError && (
                          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--error-color, #c33)' }}>
                            {dataExportError}
                          </p>
                        )}
                      </>
                  }
                </div>
              </div>
            </div>

            {/* Delete Account Section */}
            <div className="preferencesSectionContent">
              <h2 className="preferencesSectionTitle">{anon ? 'Delete My Data' : 'Delete Account'}</h2>
              <div className="preferencesGrid">
                <div className="preferenceCard">
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                    {anon
                      ? 'Permanently delete all your Downread data stored in this browser. Your subscriptions, read history, starred and read-later lists. This only affects this browser and cannot be undone.'
                      : privileged
                        ? 'Privileged accounts (Owner, Superuser, Agent) cannot be self-deleted. Ask an admin to demote this account first.'
                        : 'Permanently delete your account and all associated data. This will unsubscribe you from every feed, then erase your profile, read history, starred and read-later lists. This cannot be undone.'}
                  </p>
                  <div className="preferenceButtonRow">
                    {anon
                      ? <TextButton label="Delete my data…" type="minor" onClick={() => openAnonDataDialog('delete')} />
                      : <TextButton label="Delete account…" type="minor" enabled={accountLoaded && !privileged ? 'enabled' : 'disabled'} onClick={openDeleteDialog} />
                    }
                  </div>
                </div>
              </div>
            </div>
            <LegalFooter />
          </FlowForm>

          {/* Change Password Dialog */}
          <ModalDialog show={showPasswordDialog} title={hasPassword ? 'Change Password' : 'Set Password'} onClose={() => setShowPasswordDialog(false)}>
            <FlowForm>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
                {hasPassword && (
                  <PasswordInput
                    label="Current password"
                    value={oldPassword}
                    maxLength={PasswordLength}
                    layout="singleCol"
                    onInput={(v) => { setOldPassword(v); setPasswordStatus('idle'); }}
                  />
                )}
                <PasswordInput
                  label="New password"
                  value={newPassword}
                  errorId={newPasswordError}
                  errorTexts={passwordErrorTexts}
                  blockOnError={false}
                  maxLength={PasswordLength}
                  layout="singleCol"
                  onInput={(v) => { setNewPassword(v); if (newPasswordError) setNewPasswordError(validatePasswordField(currentEmail,v)); }}
                />
                <PasswordInput
                  label="Repeat new password"
                  value={repeatPassword}
                  errorId={repeatPasswordError}
                  errorTexts={repeatPasswordErrorTexts}
                  blockOnError={false}
                  maxLength={PasswordLength}
                  layout="singleCol"
                  onInput={(v) => { setRepeatPassword(v); if (repeatPasswordError) setRepeatPasswordError(!v ? 'missing' : newPassword != v ? 'unequal' : undefined); }}
                />
                {passwordStatus == 'wrong' && <Note type="error">Current password is incorrect.</Note>}
                {passwordStatus == 'error' && <Note type="error">Too many attempts. Please try again later.</Note>}
                {passwordStatus == 'success' && <Note type="success">Password updated successfully.</Note>}
              </div>
              <ButtonBar>
                <TextButton label="Cancel" type="minor" focussable="flow" sidewaysBehaviour="right" onClick={() => setShowPasswordDialog(false)} />
                <TextButton label={hasPassword ? "Change password" : "Set password"} type="major" focussable="sideway" busy={passwordBusy} enabled={passwordBusy ? 'disabled' : 'enabled'} onClick={handleChangePassword} />
              </ButtonBar>
            </FlowForm>
          </ModalDialog>

          {/* Change Email Dialog */}
          <ModalDialog show={showEmailDialog} title="Change Email" onClose={() => setShowEmailDialog(false)}>
            <FlowForm>
              {emailPhase == 'enterEmail' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: 0 }}>
                    We'll send half of a confirmation code to your current email and the other half to the new one. Enter both halves in the next step.
                  </p>
                  <Input
                    label="New email"
                    type="email"
                    value={newEmail}
                    errorId={newEmailError}
                    errorTexts={emailErrorTexts}
                    blockOnError={false}
                    maxLength={EmailLength}
                    layout="singleCol"
                    autoCapitalize={false}
                    autoCorrect="off"
                    onInput={(v) => { setNewEmail(v); if (newEmailError) setNewEmailError(!v.trim() ? 'missing' : validateEmail(v.trim()) ? 'invalid' : undefined); }}
                  />
                  <PasswordInput
                    label="Current password"
                    value={emailPassword}
                    errorId={emailPasswordError}
                    errorTexts={passwordErrorTexts}
                    blockOnError={false}
                    maxLength={PasswordLength}
                    layout="singleCol"
                    onInput={(v) => { setEmailPassword(v); if (emailPasswordError && v) setEmailPasswordError(undefined); setEmailStatus('idle'); }}
                  />
                  {emailStatus == 'wrongPw' && <Note type="error">Current password is incorrect.</Note>}
                  {emailStatus == 'conflict' && <Note type="error">That email is already in use.</Note>}
                  {emailStatus == 'timeout' && <Note type="error">Too many requests. Please try again later.</Note>}
                </div>
              )}
              {emailPhase == 'enterCodes' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', margin: 0 }}>
                    Check both inboxes for a 5-character code. Enter both below - order doesn't matter.
                  </p>
                  <Input
                    label="Code from current email"
                    value={emailCodeA}
                    errorId={emailCodeAError}
                    errorTexts={codeErrorTexts}
                    blockOnError={false}
                    maxLength={5}
                    layout="singleCol"
                    autoCapitalize={false}
                    autoCorrect="off"
                    spellCheck={false}
                    onInput={(v) => { setEmailCodeA(v); if (emailCodeAError) setEmailCodeAError(!v ? 'missing' : v.trim().length != 5 ? 'short' : undefined); }}
                  />
                  <Input
                    label="Code from new email"
                    value={emailCodeB}
                    errorId={emailCodeBError}
                    errorTexts={codeErrorTexts}
                    blockOnError={false}
                    maxLength={5}
                    layout="singleCol"
                    autoCapitalize={false}
                    autoCorrect="off"
                    spellCheck={false}
                    onInput={(v) => { setEmailCodeB(v); if (emailCodeBError) setEmailCodeBError(!v ? 'missing' : v.trim().length != 5 ? 'short' : undefined); }}
                  />
                  {emailStatus == 'badCodes' && <Note type="error">Those codes didn't match. Try again.</Note>}
                  {emailStatus == 'timeout' && <Note type="error">Too many attempts. Please try again later.</Note>}
                  {emailStatus == 'success' && <Note type="success">Email updated. You may need to log in again.</Note>}
                </div>
              )}
              <ButtonBar>
                <TextButton label="Cancel" type="minor" focussable="flow" sidewaysBehaviour="right" onClick={() => setShowEmailDialog(false)} />
                {emailPhase == 'enterEmail'
                  ? <TextButton label="Send codes" type="major" focussable="sideway" busy={emailBusy} enabled={emailBusy ? 'disabled' : 'enabled'} onClick={handleRequestEmailChange} />
                  : <TextButton label="Change email" type="major" focussable="sideway" busy={emailBusy} enabled={emailBusy ? 'disabled' : 'enabled'} onClick={handleChangeEmail} />
                }
              </ButtonBar>
            </FlowForm>
          </ModalDialog>

          {/* Delete Account Dialog */}
          <ModalDialog show={showDeleteDialog} title="Delete Account" onClose={() => { if (deletePhase != 'working') setShowDeleteDialog(false); }}>
            <FlowForm>
              {deletePhase == 'confirm' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
                  <p style={{ fontSize: '0.875rem', margin: 0 }}>
                    This will permanently delete your account, your <strong>{feedCount}</strong> feed subscription{feedCount == 1 ? '' : 's'}, all read markers, and your starred and read-later lists. This cannot be undone.
                  </p>
                  {hasPassword
                    ? <>
                        <PasswordInput
                          label="Confirm with your current password"
                          value={deletePassword}
                          errorId={deletePasswordError == 'missing' ? 'missing' : deletePasswordError == 'wrong' ? 'wrong' : deletePasswordError == 'timeout' ? 'timeout' : undefined}
                          errorTexts={{
                            missing: 'Please enter your password.',
                            wrong: 'Password is incorrect.',
                            timeout: 'Too many attempts. Please try again later.',
                          }}
                          blockOnError={false}
                          maxLength={PasswordLength}
                          layout="singleCol"
                          onInput={(v) => { setDeletePassword(v); if (deletePasswordError) setDeletePasswordError(undefined); }}
                        />
                        {/* Only shown for password accounts — Google/Apple-only users have no password to reset. */}
                        <div style={{ fontSize: '0.8125rem', marginTop: '-0.25rem' }}>
                          <Link to={`/forgot-password${currentEmail ? `?email=${encodeURIComponent(currentEmail)}` : ''}`}>Forgot password?</Link>
                        </div>
                      </>
                    : <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Checkbox checked={deleteChecked} onToggle={(v) => { setDeleteChecked(v); setDeleteCheckedError(false); }} />
                          <span style={{ fontSize: '0.875rem', cursor: 'pointer' }} onClick={() => { setDeleteChecked(c => !c); setDeleteCheckedError(false); }}>
                            I want to permanently delete my account
                          </span>
                        </div>
                        {deleteCheckedError && <p style={{ fontSize: '0.8125rem', color: 'var(--error-color)', margin: '0.25rem 0 0' }}>Please check the box to confirm.</p>}
                      </div>
                  }
                </div>
              )}
              {deletePhase == 'working' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.5rem 0' }}>
                  <p style={{ fontSize: '0.875rem', margin: 0 }}>
                    {deleteProgress.done < deleteProgress.total
                      ? `Unsubscribing feeds… ${deleteProgress.done} / ${deleteProgress.total}`
                      : 'Deleting account…'}
                  </p>
                  {deleteProgress.total > 0 && (
                    <div style={{ height: '6px', background: 'var(--gray-200, var(--gray-925))', borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.round((deleteProgress.done / deleteProgress.total) * 100)}%`,
                        background: 'var(--primary-color, #4a90e2)',
                        transition: 'width 0.2s ease',
                      }} />
                    </div>
                  )}
                </div>
              )}
              {deletePhase == 'success' && (
                <div style={{ padding: '0.5rem 0', maxWidth: '24rem' }}>
                  <div style={{
                    background: 'var(--primary-color-40)',
                    color: 'var(--gray-100)',
                    padding: '0.5rem',
                    borderRadius: '5px',
                    margin: '5px 0',
                    boxSizing: 'border-box',
                  }}>
                    Account deleted. Logging you out…
                  </div>
                </div>
              )}
              {deletePhase == 'error' && (
                <div style={{ padding: '0.5rem 0', maxWidth: '24rem', overflowWrap: 'break-word' }}>
                  <div style={{
                    background: '#f88',
                    color: '#000',
                    padding: '0.5rem',
                    borderRadius: '5px',
                    margin: '5px 0',
                    boxSizing: 'border-box',
                  }}>
                    Account deletion failed.
                  </div>
                  {deleteErrorMessage && (
                    <p style={{ fontSize: '0.875rem', margin: '0.5rem 0 0' }}>{deleteErrorMessage}</p>
                  )}
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: '0.5rem 0 0' }}>
                    Some feeds may have been unsubscribed already. You can close this dialog and try again.
                  </p>
                </div>
              )}
              <ButtonBar>
                {deletePhase == 'confirm' && <>
                  <TextButton label="Cancel" type="minor" focussable="flow" sidewaysBehaviour="right" onClick={() => setShowDeleteDialog(false)} />
                  <TextButton label="Delete account" type="major" focussable="sideway" onClick={handleDeleteAccount} />
                </>}
                {deletePhase == 'working' && (
                  <TextButton label="Working…" type="major" busy={true} enabled="disabled" onClick={() => {}} />
                )}
                {deletePhase == 'error' && (
                  <TextButton label="Close" type="minor" onClick={() => setShowDeleteDialog(false)} />
                )}
              </ButtonBar>
            </FlowForm>
          </ModalDialog>

          {/* Anonymous data dialog (log out / delete my data) */}
          <ModalDialog show={showAnonDataDialog} title={anonDialogMode == 'logout' ? 'Log out' : 'Delete my data'} onClose={() => { if (!anonDeleteDone) setShowAnonDataDialog(false); }}>
            <FlowForm>
              {anonDeleteDone
                ? <div style={{ padding: '0.5rem 0', maxWidth: '24rem' }}>
                    <div style={{
                      background: 'var(--primary-color-40)',
                      color: 'var(--gray-100)',
                      padding: '0.5rem',
                      borderRadius: '5px',
                      margin: '5px 0',
                      boxSizing: 'border-box',
                    }}>
                      Local data deleted.
                    </div>
                  </div>
                : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.5rem 0' }}>
                    {anonDialogMode == 'logout' && (
                      <p style={{ fontSize: '0.875rem', margin: 0 }}>
                        You're browsing Downread anonymously, so there's no account to log out of.
                      </p>
                    )}
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>
                      Your subscriptions and reading history are stored only in this browser. Do you want to delete this local data? This cannot be undone.
                    </p>
                  </div>
              }
              {!anonDeleteDone && (
                <ButtonBar>
                  <TextButton label="Cancel" type="minor" focussable="flow" sidewaysBehaviour="right" onClick={() => setShowAnonDataDialog(false)} />
                  <TextButton label="Delete data" type="major" focussable="sideway" onClick={handleDeleteAnonData} />
                </ButtonBar>
              )}
            </FlowForm>
          </ModalDialog>
        </div>
      )}
      startWithLeftCollapsed={false}
    />
    </FlowFormLeaveContext.Provider>
  );
};

export default AccountPage;
